import {
  IconBadge,
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@monksflow/monks-ui';
import type { LucideIcon } from 'lucide-react';
import { MousePointerClickIcon, SparklesIcon } from 'lucide-react';
import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import type { SelectionMode } from '../../../model/analysisFormStore';
import { StepShell } from './StepShell';

/**
 * The badge inverts with the card it sits on, because a selected card is `--accent` and a
 * badge tinted `--accent` disappears into it. No `IconBadge` variant pairs those two
 * surfaces, so the colours are set here.
 *
 * The ink follows the surface: `--accent-foreground` is what the theme guarantees for
 * `--accent`, and `--foreground` for `--background`. `--primary` would read on neither under
 * metropolis dark, where the accent is purple-900 and primary purple-500.
 */
const BADGE_ON_UNSELECTED = 'text-accent-foreground';
const BADGE_ON_SELECTED = 'bg-background text-foreground';

const PREVIOUS_KEYS = ['ArrowUp', 'ArrowLeft'];
const NEXT_KEYS = ['ArrowDown', 'ArrowRight'];

interface ModeChoiceStepProps {
  title: string;
  subtitle: string;
  autoLabel: string;
  autoDescription: string;
  manualLabel: string;
  manualDescription: string;
  /** null renders both cards unpicked, so a step cannot be passed by not deciding. */
  value: SelectionMode | null;
  onChange: (value: SelectionMode) => void;
  /** Revealed under the cards, for whatever the manual path needs. */
  children?: ReactNode;
}

/**
 * The "let IQ do it, or do it myself" question, as the two cards the audience and
 * competitors steps both ask it with: no preselection, and on the current stack rather
 * than MUI.
 *
 * A radio group rather than two toggle buttons: the cards are one either/or question, so
 * assistive tech should announce them as one of two and arrows should move between them.
 * The roles are driven onto `Item` and `ItemGroup` rather than taken from the design
 * system's RadioGroupItem, which is the 16px dot and replaces any children handed to it.
 */
export const ModeChoiceStep = ({
  title,
  subtitle,
  autoLabel,
  autoDescription,
  manualLabel,
  manualDescription,
  value,
  onChange,
  children,
}: ModeChoiceStepProps) => {
  const titleId = useId();
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const modes: Array<{
    mode: SelectionMode;
    icon: LucideIcon;
    title: string;
    description: string;
  }> = [
    { mode: 'auto', icon: SparklesIcon, title: autoLabel, description: autoDescription },
    {
      mode: 'manual',
      icon: MousePointerClickIcon,
      title: manualLabel,
      description: manualDescription,
    },
  ];

  const selectedIndex = modes.findIndex((m) => m.mode === value);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = NEXT_KEYS.includes(event.key) ? 1 : PREVIOUS_KEYS.includes(event.key) ? -1 : 0;
    if (step === 0) return;

    event.preventDefault();
    const next = (index + step + modes.length) % modes.length;
    onChange(modes[next].mode);
    cardRefs.current[next]?.focus();
  };

  return (
    <StepShell title={title} subtitle={subtitle} titleId={titleId}>
      <ItemGroup role="radiogroup" aria-labelledby={titleId} className="gap-xs">
        {modes.map(({ mode, icon: Icon, title: cardTitle, description }, index) => {
          const selected = value === mode;
          return (
            <Item
              key={mode}
              asChild
              variant={selected ? 'selected' : 'outline'}
              // A picked card already carries the accent surface, so only an unpicked one
              // has a choice left to preview under the cursor.
              className={selected ? undefined : 'hover:border-primary'}
            >
              <button
                ref={(node) => {
                  cardRefs.current[index] = node;
                }}
                type="button"
                role="radio"
                aria-checked={selected}
                // Roving tabindex: one tab stop for the group, arrows move within it. With
                // nothing picked the first card takes the stop, so the group stays reachable.
                tabIndex={selected || (selectedIndex === -1 && index === 0) ? 0 : -1}
                onClick={() => onChange(mode)}
                onKeyDown={(event) => handleKeyDown(event, index)}
                className="text-left"
              >
                <ItemMedia>
                  <IconBadge
                    data-testid={`mode-choice-icon-${mode}`}
                    variant="secondary"
                    className={`rounded-lg ${selected ? BADGE_ON_SELECTED : BADGE_ON_UNSELECTED}`}
                  >
                    <Icon />
                  </IconBadge>
                </ItemMedia>
                <ItemContent>
                  <ItemTitle>{cardTitle}</ItemTitle>
                  {/* The description is the whole of what a card offers, so it is read
                        in full rather than clamped to the primitive's two lines. */}
                  <ItemDescription className="line-clamp-none">{description}</ItemDescription>
                </ItemContent>
              </button>
            </Item>
          );
        })}
      </ItemGroup>
      {children}
    </StepShell>
  );
};
